export const LEGAL_DOCS = {
  "privacy": {
    "title": "Privacy Policy",
    "intro": "How Tripsee collects, uses, stores, and protects your personal information.",
    "meta": [
      {
        "label": "Effective Date",
        "value": "August 1st, 2025"
      }
    ],
    "blocks": [
      {
        "t": "h",
        "x": "1. Information We Collect"
      },
      {
        "t": "p",
        "x": "We collect the following categories of information:"
      },
      {
        "t": "ul",
        "items": [
          "Personal Information you provide: name, email, trip details, and optional integrations (e.g., Gmail).",
          "Automatically collected data: device identifiers, app usage logs, and location (if enabled).",
          "Travel data from third-party APIs: flight status, traffic, and mapping data from travel and mobility partners.",
          "Payment Information: processed securely by Apple/Google. Tripsee does not store card details.",
          "Analytics & Cookies: we use analytics and crash-reporting tools (e.g., Firebase, Google Analytics) to improve performance. These may use cookies or similar technologies."
        ]
      },
      {
        "t": "h",
        "x": "2. How We Use Your Information"
      },
      {
        "t": "p",
        "x": "We use your data to:"
      },
      {
        "t": "ul",
        "items": [
          "Provide, personalize, and improve Tripsee.",
          "Process payments and deliver real-time travel alerts.",
          "Generate recommendations (e.g., packing lists, predictive flight delays).",
          "Comply with legal obligations.",
          "Conduct analytics and diagnostics to improve performance."
        ]
      },
      {
        "t": "p",
        "x": "We do not sell your data."
      },
      {
        "t": "h",
        "x": "3. Automated Decision-Making & AI"
      },
      {
        "t": "p",
        "x": "Tripsee uses AI and automated systems for features such as predictive delay alerts, personalized packing suggestions, and voice assistant responses. These processes may influence recommendations shown to you but do not produce legal or similarly significant effects on your rights."
      },
      {
        "t": "h",
        "x": "4. Sharing of Information"
      },
      {
        "t": "p",
        "x": "We share data with:"
      },
      {
        "t": "ul",
        "items": [
          "Service providers: hosting, analytics, and customer support.",
          "Travel partners: airlines, airports, mapping providers, and traffic data partners.",
          "Legal authorities: where required by law."
        ]
      },
      {
        "t": "p",
        "x": "We do not share trip details with advertisers."
      },
      {
        "t": "h",
        "x": "5. Your Privacy Rights"
      },
      {
        "t": "p",
        "x": "Depending on your location, you may have rights under privacy laws (including GDPR and CCPA):"
      },
      {
        "t": "ul",
        "items": [
          "Access your data.",
          "Correct or update your data.",
          "Request deletion (“right to be forgotten”).",
          "Restrict or object to processing.",
          "Data portability (receive your data in machine-readable form).",
          "Withdraw consent at any time (e.g., disconnect Gmail, turn off location).",
          "Lodge a complaint with your local data protection authority (e.g., in the EU/UK)."
        ]
      },
      {
        "t": "p",
        "x": "You can make requests by contacting us at support@tripsee.io."
      },
      {
        "t": "h",
        "x": "6. Data Retention & Security"
      },
      {
        "t": "p",
        "x": "We retain your data only as long as necessary:"
      },
      {
        "t": "ul",
        "items": [
          "Trip history: up to 12 months unless you delete earlier.",
          "Account data: until account closure + 30 days.",
          "Legal/transactional records: as required by law.",
          "We use encryption, access controls, and monitoring to protect data."
        ]
      },
      {
        "t": "h",
        "x": "7. Children’s Privacy"
      },
      {
        "t": "p",
        "x": "Tripsee is not directed to children under:"
      },
      {
        "t": "ul",
        "items": [
          "13 years (U.S. COPPA),",
          "16 years (EEA), or",
          "local equivalent age of digital consent."
        ]
      },
      {
        "t": "p",
        "x": "We do not knowingly collect information from children below these ages."
      },
      {
        "t": "h",
        "x": "8. International Transfers"
      },
      {
        "t": "p",
        "x": "Your information may be stored or processed outside your country, including in the United States. Where required, we implement safeguards such as Standard Contractual Clauses (SCCs) for cross-border data transfers."
      },
      {
        "t": "h",
        "x": "9. Changes to Policy"
      },
      {
        "t": "p",
        "x": "We may update this Privacy Policy. Updates will be posted in-app and on tripsee.io, with the “Effective Date” updated accordingly."
      },
      {
        "t": "h",
        "x": "10. Contact Us"
      },
      {
        "t": "p",
        "x": "Tripsee LLC – Attn: Privacy Team"
      },
      {
        "t": "p",
        "x": "support@tripsee.io"
      }
    ]
  },
  "disclaimer": {
    "title": "Legal Disclaimer",
    "intro": "Important legal notices regarding the information Tripsee provides.",
    "meta": [
      {
        "label": "Effective Date",
        "value": "March 1st, 2025"
      },
      {
        "label": "Last Updated",
        "value": "November 3rd, 2025"
      }
    ],
    "blocks": [
      {
        "t": "p",
        "x": "This Full Disclaimer (“**Disclaimer** ”) explains important limitations and conditions for use of the Tripsee mobile and web applications, websites, services, and related features (collectively, the “**Services** ”) provided by Tripsee LLC (“**Tripsee** ,” “**we** ,” “**us** ,” or “**our** ”). By accessing or using the Services, you (“**you** ” or “**User** ”) acknowledge and agree to this Disclaimer in addition to our **Terms of Use** and **Privacy Policy** (collectively, the “**Agreements** ”). If you do not agree, do not use the Services."
      },
      {
        "t": "h",
        "x": "1. Informational Use Only; No Guarantees"
      },
      {
        "t": "p",
        "x": "The Services provide **estimates, forecasts, and informational content only** (including but not limited to suggested departure times, route guidance, airport timing, queue estimates, and flight or traffic information). **Actual conditions vary** due to factors outside our control (e.g., traffic, weather, operational changes, security lines, strikes, outages, airline/airport decisions, user device performance). **All information is provided “AS IS” without warranties** and **does not guarantee** any particular timing, arrival, or outcome, including catching a flight or meeting a schedule."
      },
      {
        "t": "h",
        "x": "2. No Professional, Safety, or Emergency Advice"
      },
      {
        "t": "p",
        "x": "Tripsee does **not** provide professional advice of any kind (legal, safety, travel agency, logistics, or otherwise). The Services are **not intended for emergency or safety-critical use**. For emergencies, call your local emergency number and follow official instructions. Always verify critical details with your airline, airport, transit provider, or relevant authorities."
      },
      {
        "t": "h",
        "x": "3. User Responsibility; Independent Judgment"
      },
      {
        "t": "p",
        "x": "You are solely responsible for **planning, decisions, and outcomes** related to your travel, including allowing extra time; verifying flight status, gate changes, check-in and security requirements; and complying with all laws, regulations, ticket/visa/passport rules, and carrier policies. Always apply independent judgment and cross-check information with official sources."
      },
      {
        "t": "h",
        "x": "4. Third-Party Data, Services, and Integrations"
      },
      {
        "t": "p",
        "x": "Tripsee may display or rely on data and services from third parties (e.g. flight data, maps, airlines, airports, weather providers). We **do not control, endorse, or guarantee** third-party content, availability, accuracy, or policies. Third-party terms and privacy policies may apply. **Outages, latency, or errors** from third parties may affect the Services."
      },
      {
        "t": "h",
        "x": "5. Beta, Experimental, and Predictive Features"
      },
      {
        "t": "p",
        "x": "Some features may be **beta, experimental, or predictive**(including risk scores, delay likelihood, recommended departure times). Such features are inherently uncertain and may be modified, suspended, or removed at any time. Use at your own risk."
      },
      {
        "t": "h",
        "x": "6. Device, Connectivity, and Location Limitations"
      },
      {
        "t": "p",
        "x": "Service quality may depend on your **device settings, permissions (e.g., location), OS version, battery, sensors, and connectivity**. Failure to grant permissions or maintain connectivity can degrade or disable features. Location accuracy varies and may be unavailable or imprecise."
      },
      {
        "t": "h",
        "x": "7. Notifications and Reminders"
      },
      {
        "t": "p",
        "x": "Notifications (e.g., “leave now,” delays, gate changes) may be delayed, filtered, misdelivered, or missed due to device/OS/network factors. **Do not rely solely on notifications** for time-critical decisions. Always confirm details with official sources."
      },
      {
        "t": "h",
        "x": "8. User Conduct and Prohibited Uses"
      },
      {
        "t": "p",
        "x": "You agree **not** to misuse the Services, including:"
      },
      {
        "t": "ul",
        "items": [
          "Using the Services in a manner that is unsafe, unlawful, or violates      carrier/airport/authority rules.",
          "Circumventing access controls; reverse engineering; scraping at scale; or interfering with service integrity.",
          "Submitting unlawful, misleading, or harmful content. We may suspend or terminate access for violations, as described in our Terms of Use."
        ]
      },
      {
        "t": "h",
        "x": "9. No Warranties"
      },
      {
        "t": "p",
        "x": "To the **maximum extent permitted by law** , the Services are provided **“AS IS” and “AS AVAILABLE”** , **without warranties of any kind** , whether express, implied, or statutory, including **merchantability, fitness for a particular purpose, accuracy, quiet enjoyment, and non-infringement**. We do not warrant that the Services will be **error-free, uninterrupted, timely, accurate, or secure** , or that defects will be corrected."
      },
      {
        "t": "h",
        "x": "10. Limitation of Liability"
      },
      {
        "t": "p",
        "x": "To the **maximum extent permitted by law** , **Tripsee and its affiliates, officers, directors, employees, contractors, partners, licensors, and suppliers** shall **not** be liable for **indirect, incidental, special, consequential, exemplary, punitive, or increased damages** , or for **lost profits, lost data, loss of goodwill, business interruption, personal injury, property damage, missed flights, missed connections, or other travel-related losses** , arising out of or related to your use of (or inability to use) the Services, even if advised of the possibility of such damages.\nWhere liability cannot be disclaimed, the **total aggregate liability** of Tripsee and its affiliates for any claim shall **not exceed the greater of (a) USD $100 or (b) the amount you paid to Tripsee for the Services in the 12 months preceding the claim**. Some jurisdictions do not allow certain limitations; in those cases, the limitations apply to the **fullest extent permitted**."
      },
      {
        "t": "h",
        "x": "11. Indemnification"
      },
      {
        "t": "p",
        "x": "You agree to **defend, indemnify, and hold harmless** Tripsee and its affiliates, officers, directors, employees, contractors, partners, licensors, and suppliers from and against any claims, liabilities, damages, losses, and expenses (including **reasonable attorneys’ fees and costs**) arising out of or related to: (a) your use of the Services; (b) your violation of the Agreements or applicable law; (c) your content or data you provide; or (d) your travel decisions or actions."
      },
      {
        "t": "h",
        "x": "12. Changes, Availability, and Force Majeure"
      },
      {
        "t": "p",
        "x": "We may **modify, suspend, or discontinue** any part of the Services at any time without notice. We are **not liable** for any failure or delay due to causes beyond our reasonable control, including **acts of God, natural disasters, strikes, labor disputes, war, terrorism, civil disorder, embargoes, power or internet outages, third-party failures, or changes in laws or policies**."
      },
      {
        "t": "h",
        "x": "13. International Use and Compliance"
      },
      {
        "t": "p",
        "x": "You are responsible for compliance with **local laws, travel restrictions, import/export controls** , and airline/airport rules wherever you use the Services. Access to the Services may be **restricted or prohibited** in some locations."
      },
      {
        "t": "h",
        "x": "14. Privacy; Data Sources"
      },
      {
        "t": "p",
        "x": "Our handling of personal data is described in our **Privacy Policy**. Some features rely on **permissions** (e.g., location, notifications, calendar, email parsing of receipts/confirmations) and on **third-party data**. By enabling such features, you authorize Tripsee to process data as described in the Privacy Policy. **Do not enable features** you are not comfortable with."
      },
      {
        "t": "h",
        "x": "15. External Links and User Content"
      },
      {
        "t": "p",
        "x": "Links to external sites or resources do not imply endorsement. We are not responsible for **content, accuracy, or practices** of third-party sites. You are responsible for any **content you submit** and must ensure you have the rights to share it."
      },
      {
        "t": "h",
        "x": "16. Children and Eligibility"
      },
      {
        "t": "p",
        "x": "The Services are intended for users who meet the **minimum age** specified in our Terms of Use and applicable laws. Do not use the Services if you are **under the applicable age** or otherwise prohibited by law or contract."
      },
      {
        "t": "h",
        "x": "17. Governing Law; Dispute Resolution"
      },
      {
        "t": "p",
        "x": "This Disclaimer and the Agreements are governed by and construed under the laws of the **State of Delaware** , United States, without regard to conflict-of-law principles. Any dispute arising out of or relating to the Services shall be resolved exclusively in the state or federal courts located in **Wilmington, Delaware** , and you consent to the personal jurisdiction of such courts."
      },
      {
        "t": "p",
        "x": "Alternatively, Tripsee may elect to resolve disputes through binding arbitration under the rules of the American Arbitration Association (AAA), held in Delaware, and judgment on the award may be entered in any court of competent jurisdiction."
      },
      {
        "t": "h",
        "x": "18. Severability; Waiver; Assignment"
      },
      {
        "t": "p",
        "x": "If any provision is found unenforceable, the remaining provisions remain in effect. Failure to enforce a provision is **not** a waiver. You may **not** assign your rights or obligations without our prior written consent; we may assign as permitted by law."
      },
      {
        "t": "h",
        "x": "19. Updates to this Disclaimer"
      },
      {
        "t": "p",
        "x": "We may update this Disclaimer from time to time. **Material changes** will be noted by updating the “Last Updated” date and, where required, by additional notice. Continued use after changes indicates acceptance."
      },
      {
        "t": "h",
        "x": "20. Contact"
      },
      {
        "t": "p",
        "x": "Questions about this Disclaimer can be directed to:"
      },
      {
        "t": "p",
        "x": "**Tripsee LLC**\n**Email:** support@tripsee.io\n**Website:**https://tripsee.io"
      }
    ]
  },
  "termsOfUse": {
    "title": "Terms of Use",
    "intro": "The rules that govern your use of the Tripsee website and services.",
    "meta": [
      {
        "label": "Effective Date",
        "value": "March 1, 2025"
      },
      {
        "label": "Last Updated",
        "value": "May 5, 2026"
      }
    ],
    "blocks": [
      {
        "t": "p",
        "x": "Welcome to Tripsee! These Terms of Use (“Terms”) govern your access to and use of the Tripsee mobile applications, website, and related services (collectively, the “Service”). By accessing or using the Service, you agree to be bound by these Terms. If you do not agree, you must discontinue use of Tripsee."
      },
      {
        "t": "p",
        "x": "These Terms constitute a binding agreement between you (“User,” “you,” or “your”) and **Tripsee LLC** (“Tripsee,” “we,” “our,” or “us”)."
      },
      {
        "t": "h",
        "x": "1. Description of Services"
      },
      {
        "t": "p",
        "x": "Tripsee provides tools and insights to help travelers better manage airport navigation, door-to-gate timing, flight-related predictions, and trip planning. While Tripsee may provide air-travel insights, Tripsee is **not** :"
      },
      {
        "t": "ul",
        "items": [
          "A travel agency"
        ]
      },
      {
        "t": "ul",
        "items": [
          "A booking platform"
        ]
      },
      {
        "t": "ul",
        "items": [
          "An airline or airport service provider"
        ]
      },
      {
        "t": "ul",
        "items": [
          "A provider of guaranteed arrival or departure times"
        ]
      },
      {
        "t": "p",
        "x": "All predictions and insights are estimates only and may be affected by real-time conditions outside our control."
      },
      {
        "t": "h",
        "x": "2. Eligibility"
      },
      {
        "t": "p",
        "x": "To use Tripsee, you must:"
      },
      {
        "t": "ul",
        "items": [
          "Be at least 13 years old (or older if required in your jurisdiction)"
        ]
      },
      {
        "t": "ul",
        "items": [
          "Have the legal capacity to enter into binding agreements"
        ]
      },
      {
        "t": "ul",
        "items": [
          "Use the Service in compliance with applicable laws"
        ]
      },
      {
        "t": "p",
        "x": "If you are using Tripsee on behalf of an organization, you represent that you are authorized to bind that organization to these Terms."
      },
      {
        "t": "h",
        "x": "3. Account Registration & Security"
      },
      {
        "t": "p",
        "x": "Some features require creating an account. You agree to:"
      },
      {
        "t": "ul",
        "items": [
          "Provide accurate and complete information"
        ]
      },
      {
        "t": "ul",
        "items": [
          "Keep your password and login credentials confidential"
        ]
      },
      {
        "t": "ul",
        "items": [
          "Accept responsibility for all activity occurring under your account"
        ]
      },
      {
        "t": "ul",
        "items": [
          "Notify us immediately of unauthorized access"
        ]
      },
      {
        "t": "p",
        "x": "Tripsee is not liable for losses arising from unauthorized account use."
      },
      {
        "t": "h",
        "x": "4. Subscription Terms & Billing (First Class)"
      },
      {
        "t": "p",
        "x": "Tripsee offers an optional paid subscription called **First Class** (“Subscription”). By purchasing, activating, or using the Subscription, you agree to the following:"
      },
      {
        "t": "h",
        "x": "4.1 Subscription Duration & Price"
      },
      {
        "t": "p",
        "x": "First Class is offered as a reoccurring subscription**** basis at **$4.99 per month or $29.99 USD per year** , or the equivalent amount in your local currency. Taxes or regional adjustments may apply."
      },
      {
        "t": "h",
        "x": "4.2 Auto-Renewal"
      },
      {
        "t": "p",
        "x": "Your Subscription **automatically renews** each year unless you cancel at least **24 hours before the end of the current billing cycle**."
      },
      {
        "t": "p",
        "x": "Your account will be charged for renewal within 24 hours prior to the renewal date."
      },
      {
        "t": "h",
        "x": "4.3 Payment Processing"
      },
      {
        "t": "p",
        "x": "All billing is handled by the platform through which you subscribed:"
      },
      {
        "t": "ul",
        "items": [
          "**Apple App Store / Apple ID account** (iOS users)"
        ]
      },
      {
        "t": "ul",
        "items": [
          "**Google Play Store account** (Android users)"
        ]
      },
      {
        "t": "p",
        "x": "Tripsee does **not** process or store your payment information."
      },
      {
        "t": "h",
        "x": "4.4 Managing or Cancelling the Subscription"
      },
      {
        "t": "p",
        "x": "You can cancel or manage your Subscription at any time through:"
      },
      {
        "t": "ul",
        "items": [
          "**iOS:** Settings → Apple ID → Subscriptions"
        ]
      },
      {
        "t": "ul",
        "items": [
          "**Android:** Google Play Store → Profile → Payments & Subscriptions"
        ]
      },
      {
        "t": "p",
        "x": "Deleting the Tripsee app **does not cancel** your Subscription."
      },
      {
        "t": "h",
        "x": "4.5 Subscription Features"
      },
      {
        "t": "p",
        "x": "First Class includes premium features, including but not limited to:"
      },
      {
        "t": "ul",
        "items": [
          "Increased monthly **flight monitors**"
        ]
      },
      {
        "t": "ul",
        "items": [
          "Enhanced travel insights"
        ]
      },
      {
        "t": "ul",
        "items": [
          "Expanded airport and flight tracking capabilities"
        ]
      },
      {
        "t": "ul",
        "items": [
          "Additional premium tools as the Service evolves"
        ]
      },
      {
        "t": "p",
        "x": "Tripsee may update or modify the features included in First Class."
      },
      {
        "t": "h",
        "x": "4.6 Usage Limits"
      },
      {
        "t": "p",
        "x": "The First Class Subscription includes access to up to 10 trips per billing cycle (monthly equivalent for annual plans)."
      },
      {
        "t": "p",
        "x": "A “trip” is defined as a single travel timeline beginning at the user’s point of origin and ending upon arrival at the departure airport. Roundtrip travel counts as two trips."
      },
      {
        "t": "p",
        "x": "Tripsee reserves the right to modify usage limits or definitions with notice as described in these Terms."
      },
      {
        "t": "h",
        "x": "5. Free Usage (Non-Subscription Access)"
      },
      {
        "t": "p",
        "x": "Tripsee may provide limited access to First Class features without requiring a Subscription."
      },
      {
        "t": "p",
        "x": "Upon account creation, users may receive a limited number of complimentary “trips” to experience First Class features at no cost and with no obligation to subscribe."
      },
      {
        "t": "p",
        "x": "The number of complimentary trips and eligibility criteria may be modified at any time.."
      },
      {
        "t": "h",
        "x": "5.1 Trial Conversion"
      },
      {
        "t": "p",
        "x": "At the end of the free trial period, your Subscription will **automatically convert** to the annual paid plan unless you cancel before the trial ends."
      },
      {
        "t": "h",
        "x": "5.2 Trial Eligibility"
      },
      {
        "t": "p",
        "x": "Free trials are typically limited to new users but may vary by promotion. Tripsee reserves the right to restrict or revoke free trial eligibility to prevent abuse."
      },
      {
        "t": "h",
        "x": "5.3 Forfeiture of Unused Trial Time"
      },
      {
        "t": "p",
        "x": "Any unused portion of a free trial will be **forfeited** once you purchase a Subscription."
      },
      {
        "t": "h",
        "x": "6. Refunds & Cancellation Policy"
      },
      {
        "t": "h",
        "x": "6.1 No Refunds"
      },
      {
        "t": "p",
        "x": "Except where required by law, Tripsee does **not** issue refunds or credits for unused Subscription time once the annual billing cycle begins."
      },
      {
        "t": "h",
        "x": "6.2 Platform-Managed Refunds"
      },
      {
        "t": "p",
        "x": "Any refund request must be processed through the platform where the Subscription was purchased:"
      },
      {
        "t": "ul",
        "items": [
          "Apple App Store (iOS)"
        ]
      },
      {
        "t": "ul",
        "items": [
          "Google Play Store (Android)"
        ]
      },
      {
        "t": "p",
        "x": "Tripsee cannot issue refunds directly."
      },
      {
        "t": "h",
        "x": "6.3 Access After Cancellation"
      },
      {
        "t": "p",
        "x": "If you cancel your Subscription, you will retain access to First Class features until the end of your current billing period."
      },
      {
        "t": "p",
        "x": "After that, your account will revert to the Free tier."
      },
      {
        "t": "h",
        "x": "7. Acceptable Use"
      },
      {
        "t": "p",
        "x": "You agree not to:"
      },
      {
        "t": "ul",
        "items": [
          "Misuse or interfere with the Service"
        ]
      },
      {
        "t": "ul",
        "items": [
          "Attempt to access data belonging to other users"
        ]
      },
      {
        "t": "ul",
        "items": [
          "Use automated tools (scrapers, bots) without permission"
        ]
      },
      {
        "t": "ul",
        "items": [
          "Reverse-engineer, modify, or copy the app or website"
        ]
      },
      {
        "t": "ul",
        "items": [
          "Use the Service for unlawful, harmful, or fraudulent activities"
        ]
      },
      {
        "t": "p",
        "x": "Tripsee may terminate or restrict access for violations."
      },
      {
        "t": "h",
        "x": "8. Intellectual Property"
      },
      {
        "t": "p",
        "x": "Tripsee and all associated trademarks, logos, designs, graphics, and software are the property of Tripsee LLC or its licensors."
      },
      {
        "t": "p",
        "x": "You may not reproduce, distribute, modify, or create derivative works without express permission."
      },
      {
        "t": "h",
        "x": "9. Disclaimers"
      },
      {
        "t": "p",
        "x": "Tripsee provides the Service **“as is”** without warranties of any kind."
      },
      {
        "t": "p",
        "x": "Tripsee does not guarantee:"
      },
      {
        "t": "ul",
        "items": [
          "Accuracy of travel predictions"
        ]
      },
      {
        "t": "ul",
        "items": [
          "Real-time airport data"
        ]
      },
      {
        "t": "ul",
        "items": [
          "Flight timing, delays, or operational decisions"
        ]
      },
      {
        "t": "ul",
        "items": [
          "Availability or error-free operation of the Service"
        ]
      },
      {
        "t": "p",
        "x": "Use Tripsee at your own risk."
      },
      {
        "t": "h",
        "x": "10. Limitation of Liability"
      },
      {
        "t": "p",
        "x": "To the maximum extent permitted by law, Tripsee is **not liable** for:"
      },
      {
        "t": "ul",
        "items": [
          "Indirect, incidental, or consequential damages"
        ]
      },
      {
        "t": "ul",
        "items": [
          "Data loss or corruption"
        ]
      },
      {
        "t": "ul",
        "items": [
          "Travel delays, missed flights, or schedule disruptions"
        ]
      },
      {
        "t": "ul",
        "items": [
          "Third-party service failures (APIs, airlines, airports, weather providers)"
        ]
      },
      {
        "t": "p",
        "x": "Tripsee’s total liability for any claims shall not exceed the amount you paid for the Subscription during the 12 months prior to the claim."
      },
      {
        "t": "h",
        "x": "11. Modifications to the Service or Terms"
      },
      {
        "t": "p",
        "x": "We may update these Terms or make changes to the Service, including pricing or Subscription features."
      },
      {
        "t": "p",
        "x": "If changes materially affect your Subscription, we will notify you in advance."
      },
      {
        "t": "p",
        "x": "Continued use of Tripsee after such changes constitutes acceptance."
      },
      {
        "t": "h",
        "x": "12. Privacy Policy"
      },
      {
        "t": "p",
        "x": "Tripsee’s Privacy Policy explains how we collect, use, and store your information and is incorporated by reference into these Terms."
      },
      {
        "t": "p",
        "x": "It is available at: **https://tripsee.io/privacy-policy**"
      },
      {
        "t": "h",
        "x": "13. Governing Law"
      },
      {
        "t": "p",
        "x": "These Terms are governed by the laws of the State of [Insert Jurisdiction], without regard to conflict-of-law principles."
      },
      {
        "t": "p",
        "x": "You agree to submit to the exclusive jurisdiction of courts located in that state."
      },
      {
        "t": "h",
        "x": "14. Contact Us"
      },
      {
        "t": "p",
        "x": "For questions or support, please contact:"
      },
      {
        "t": "p",
        "x": "**support@tripsee.io**\n**https://tripsee.io**"
      }
    ]
  },
  "eula": {
    "title": "End User License Agreement",
    "intro": "The license terms for installing and using the Tripsee mobile application.",
    "meta": [],
    "blocks": [
      {
        "t": "p",
        "x": "Apps made available through the App Store are licensed, not sold, to you. Your license to each App is subject to your prior acceptance of either this Licensed Application End User License Agreement (“Standard EULA”), or a custom end user license agreement between you and the Application Provider (“Custom EULA”), if one is provided. Your license to any Apple App under this Standard EULA or Custom EULA is granted by Apple, and your license to any Third Party App under this Standard EULA or Custom EULA is granted by the Application Provider of that Third Party App. Any App that is subject to this Standard EULA is referred to herein as the “Licensed Application.” The Application Provider or Apple as applicable (“Licensor”) reserves all rights in and to the Licensed Application not expressly granted to you under this Standard EULA."
      },
      {
        "t": "p",
        "x": "a. Scope of License: Licensor grants to you a nontransferable license to use the Licensed Application on any Apple-branded products that you own or control and as permitted by the Usage Rules. The terms of this Standard EULA will govern any content, materials, or services accessible from or purchased within the Licensed Application as well as upgrades provided by Licensor that replace or supplement the original Licensed Application, unless such upgrade is accompanied by a Custom EULA. Except as provided in the Usage Rules, you may not distribute or make the Licensed Application available over a network where it could be used by multiple devices at the same time. You may not transfer, redistribute or sublicense the Licensed Application and, if you sell your Apple Device to a third party, you must remove the Licensed Application from the Apple Device before doing so. You may not copy (except as permitted by this license and the Usage Rules), reverse-engineer, disassemble, attempt to derive the source code of, modify, or create derivative works of the Licensed Application, any updates, or any part thereof (except as and only to the extent that any foregoing restriction is prohibited by applicable law or to the extent as may be permitted by the licensing terms governing use of any open-sourced components included with the Licensed Application)."
      },
      {
        "t": "p",
        "x": "b. Consent to Use of Data: You agree that Licensor may collect and use technical data and related information—including but not limited to technical information about your device, system and application software, and peripherals—that is gathered periodically to facilitate the provision of software updates, product support, and other services to you (if any) related to the Licensed Application. Licensor may use this information, as long as it is in a form that does not personally identify you, to improve its products or to provide services or technologies to you."
      },
      {
        "t": "p",
        "x": "c. Termination. This Standard EULA is effective until terminated by you or Licensor. Your rights under this Standard EULA will terminate automatically if you fail to comply with any of its terms."
      },
      {
        "t": "p",
        "x": "d. External Services. The Licensed Application may enable access to Licensor’s and/or third-party services and websites (collectively and individually, \"External Services\"). You agree to use the External Services at your sole risk. Licensor is not responsible for examining or evaluating the content or accuracy of any third-party External Services, and shall not be liable for any such third-party External Services. Data displayed by any Licensed Application or External Service, including but not limited to financial, medical and location information, is for general informational purposes only and is not guaranteed by Licensor or its agents. You will not use the External Services in any manner that is inconsistent with the terms of this Standard EULA or that infringes the intellectual property rights of Licensor or any third party. You agree not to use the External Services to harass, abuse, stalk, threaten or defame any person or entity, and that Licensor is not responsible for any such use. External Services may not be available in all languages or in your Home Country, and may not be appropriate or available for use in any particular location. To the extent you choose to use such External Services, you are solely responsible for compliance with any applicable laws. Licensor reserves the right to change, suspend, remove, disable or impose access restrictions or limits on any External Services at any time without notice or liability to you."
      },
      {
        "t": "p",
        "x": "e. NO WARRANTY: YOU EXPRESSLY ACKNOWLEDGE AND AGREE THAT USE OF THE LICENSED APPLICATION IS AT YOUR SOLE RISK. TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, THE LICENSED APPLICATION AND ANY SERVICES PERFORMED OR PROVIDED BY THE LICENSED APPLICATION ARE PROVIDED \"AS IS\" AND “AS AVAILABLE,” WITH ALL FAULTS AND WITHOUT WARRANTY OF ANY KIND, AND LICENSOR HEREBY DISCLAIMS ALL WARRANTIES AND CONDITIONS WITH RESPECT TO THE LICENSED APPLICATION AND ANY SERVICES, EITHER EXPRESS, IMPLIED, OR STATUTORY, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES AND/OR CONDITIONS OF MERCHANTABILITY, OF SATISFACTORY QUALITY, OF FITNESS FOR A PARTICULAR PURPOSE, OF ACCURACY, OF QUIET ENJOYMENT, AND OF NONINFRINGEMENT OF THIRD-PARTY RIGHTS. NO ORAL OR WRITTEN INFORMATION OR ADVICE GIVEN BY LICENSOR OR ITS AUTHORIZED REPRESENTATIVE SHALL CREATE A WARRANTY. SHOULD THE LICENSED APPLICATION OR SERVICES PROVE DEFECTIVE, YOU ASSUME THE ENTIRE COST OF ALL NECESSARY SERVICING, REPAIR, OR CORRECTION. SOME JURISDICTIONS DO NOT ALLOW THE EXCLUSION OF IMPLIED WARRANTIES OR LIMITATIONS ON APPLICABLE STATUTORY RIGHTS OF A CONSUMER, SO THE ABOVE EXCLUSION AND LIMITATIONS MAY NOT APPLY TO YOU."
      },
      {
        "t": "p",
        "x": "f. Limitation of Liability. TO THE EXTENT NOT PROHIBITED BY LAW, IN NO EVENT SHALL LICENSOR BE LIABLE FOR PERSONAL INJURY OR ANY INCIDENTAL, SPECIAL, INDIRECT, OR CONSEQUENTIAL DAMAGES WHATSOEVER, INCLUDING, WITHOUT LIMITATION, DAMAGES FOR LOSS OF PROFITS, LOSS OF DATA, BUSINESS INTERRUPTION, OR ANY OTHER COMMERCIAL DAMAGES OR LOSSES, ARISING OUT OF OR RELATED TO YOUR USE OF OR INABILITY TO USE THE LICENSED APPLICATION, HOWEVER CAUSED, REGARDLESS OF THE THEORY OF LIABILITY (CONTRACT, TORT, OR OTHERWISE) AND EVEN IF LICENSOR HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES. SOME JURISDICTIONS DO NOT ALLOW THE LIMITATION OF LIABILITY FOR PERSONAL INJURY, OR OF INCIDENTAL OR CONSEQUENTIAL DAMAGES, SO THIS LIMITATION MAY NOT APPLY TO YOU. In no event shall Licensor’s total liability to you for all damages (other than as may be required by applicable law in cases involving personal injury) exceed the amount of fifty dollars ($50.00). The foregoing limitations will apply even if the above stated remedy fails of its essential purpose."
      },
      {
        "t": "p",
        "x": "g. You may not use or otherwise export or re-export the Licensed Application except as authorized by United States law and the laws of the jurisdiction in which the Licensed Application was obtained. In particular, but without limitation, the Licensed Application may not be exported or re-exported (a) into any U.S.-embargoed countries or (b) to anyone on the U.S. Treasury Department's Specially Designated Nationals List or the U.S. Department of Commerce Denied Persons List or Entity List. By using the Licensed Application, you represent and warrant that you are not located in any such country or on any such list. You also agree that you will not use these products for any purposes prohibited by United States law, including, without limitation, the development, design, manufacture, or production of nuclear, missile, or chemical or biological weapons."
      },
      {
        "t": "p",
        "x": "h. The Licensed Application and related documentation are \"Commercial Items\", as that term is defined at 48 C.F.R. §2.101, consisting of \"Commercial Computer Software\" and \"Commercial Computer Software Documentation\", as such terms are used in 48 C.F.R. §12.212 or 48 C.F.R. §227.7202, as applicable. Consistent with 48 C.F.R. §12.212 or 48 C.F.R. §227.7202-1 through 227.7202-4, as applicable, the Commercial Computer Software and Commercial Computer Software Documentation are being licensed to U.S. Government end users (a) only as Commercial Items and (b) with only those rights as are granted to all other end users pursuant to the terms and conditions herein. Unpublished-rights reserved under the copyright laws of the United States."
      },
      {
        "t": "p",
        "x": "i. Except to the extent expressly provided in the following paragraph, this Agreement and the relationship between you and Apple shall be governed by the laws of the State of California, excluding its conflicts of law provisions. You and Apple agree to submit to the personal and exclusive jurisdiction of the courts located within the county of Santa Clara, California, to resolve any dispute or claim arising from this Agreement. If (a) you are not a U.S. citizen; (b) you do not reside in the U.S.; (c) you are not accessing the Service from the U.S.; and (d) you are a citizen of one of the countries identified below, you hereby agree that any dispute or claim arising from this Agreement shall be governed by the applicable law set forth below, without regard to any conflict of law provisions, and you hereby irrevocably submit to the non-exclusive jurisdiction of the courts located in the state, province or country identified below whose law governs:"
      },
      {
        "t": "p",
        "x": "If you are a citizen of any European Union country or Switzerland, Norway or Iceland, the governing law and forum shall be the laws and courts of your usual place of residence."
      },
      {
        "t": "p",
        "x": "Specifically excluded from application to this Agreement is that law known as the United Nations Convention on the International Sale of Goods."
      }
    ]
  }
};
